/* eslint-disable react/no-unescaped-entities */
import React, { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SITE } from "@/constants/site";

const SERVICES = [
    "Business Start-Up Consulting",
    "Nonprofit Planning & Development",
    "Real Estate Investment Strategy",
    "Grant Writing & Fundraising",
    "Marketing & Promotion",
    "Branding & Business Identity",
    "Website Design & Online Presence",
    "Behavioral Health & Program Strategy",
    "Professional Business Planning",
    "Organizational Development",
    "Leadership Training",
    "Board Development",
    "New Construction Consulting",
    "Other / Not Sure",
];

const BUDGETS = [
    "Under $2,500",
    "$2,500 – $5,000",
    "$5,000 – $15,000",
    "$15,000 – $50,000",
    "$50,000+",
    "Not sure yet",
];

const TIMELINES = [
    "Immediate (this month)",
    "1–3 months",
    "3–6 months",
    "6+ months",
    "Just exploring",
];

export default function Contact() {
    const params = new URLSearchParams(useLocation().search);
    const defaultService = params.get("service") || "";

    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        organization: "",
        service_needed: defaultService,
        budget_range: "",
        timeline: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await api.post("/leads", form);
            setSubmitted(true);
            toast.success("Thanks — Reico will be in touch shortly.");
        } catch (err) {
            const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <section className="bg-[#0A192F] text-white py-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <span className="overline">Contact</span>
                        <h1 className="font-serif text-5xl sm:text-6xl font-bold mt-4 leading-tight">
                            Schedule Your Consultation{" "}
                            <span className="italic text-[#D4AF37]">Today.</span>
                        </h1>
                        <p className="mt-6 text-slate-300 max-w-2xl leading-relaxed">
                            Tell us about your project. We'll respond within
                            one business day with the next step.
                        </p>
                    </div>
                    <div className="lg:col-span-5 grid gap-4">
                        <a
                            href={`tel:${SITE.phoneLink}`}
                            data-testid="contact-phone-link"
                            className="flex items-center gap-4 bg-white/5 border border-white/10 hover:border-[#D4AF37] p-5 transition-colors"
                        >
                            <Phone className="w-5 h-5 text-[#D4AF37]" />
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                                    Call directly
                                </div>
                                <div className="font-serif text-2xl font-bold mt-1">
                                    {SITE.phone}
                                </div>
                            </div>
                        </a>
                        <a
                            href={`mailto:${SITE.email}`}
                            data-testid="contact-email-link"
                            className="flex items-center gap-4 bg-white/5 border border-white/10 hover:border-[#D4AF37] p-5 transition-colors"
                        >
                            <Mail className="w-5 h-5 text-[#D4AF37]" />
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                                    Email
                                </div>
                                <div className="font-semibold mt-1 break-all">
                                    {SITE.email}
                                </div>
                            </div>
                        </a>
                    </div>
                </div>
            </section>

            <section className="py-24">
                <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
                    {submitted ? (
                        <div className="exec-card p-12 text-center" data-testid="contact-success">
                            <CheckCircle2 className="w-12 h-12 text-[#D4AF37] mx-auto mb-6" />
                            <h2 className="font-serif text-3xl font-bold text-[#0A192F]">
                                Message received.
                            </h2>
                            <p className="mt-4 text-slate-600 max-w-md mx-auto">
                                Reico will personally review your request and
                                follow up within one business day at the
                                contact you provided.
                            </p>
                            <Link
                                to="/"
                                className="mt-8 inline-flex items-center gap-2 text-[#0A192F] font-semibold link-underline pb-1"
                            >
                                Return home <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit} className="grid md:grid-cols-2 gap-10" data-testid="contact-form">
                            <div>
                                <label className="exec-input-label">Name</label>
                                <input
                                    required
                                    type="text"
                                    data-testid="contact-input-name"
                                    className="exec-input"
                                    value={form.name}
                                    onChange={(e) => update("name", e.target.value)}
                                    placeholder="Full name"
                                />
                            </div>
                            <div>
                                <label className="exec-input-label">Email</label>
                                <input
                                    required
                                    type="email"
                                    data-testid="contact-input-email"
                                    className="exec-input"
                                    value={form.email}
                                    onChange={(e) => update("email", e.target.value)}
                                    placeholder="you@example.com"
                                />
                            </div>
                            <div>
                                <label className="exec-input-label">Phone</label>
                                <input
                                    type="tel"
                                    data-testid="contact-input-phone"
                                    className="exec-input"
                                    value={form.phone}
                                    onChange={(e) => update("phone", e.target.value)}
                                    placeholder="(865) 555-0123"
                                />
                            </div>
                            <div>
                                <label className="exec-input-label">Organization / Business</label>
                                <input
                                    type="text"
                                    data-testid="contact-input-org"
                                    className="exec-input"
                                    value={form.organization}
                                    onChange={(e) => update("organization", e.target.value)}
                                    placeholder="Optional"
                                />
                            </div>
                            <div>
                                <label className="exec-input-label">Service Needed</label>
                                <select
                                    data-testid="contact-input-service"
                                    className="exec-input"
                                    value={form.service_needed}
                                    onChange={(e) => update("service_needed", e.target.value)}
                                >
                                    <option value="">Select a service…</option>
                                    {SERVICES.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="exec-input-label">Budget Range</label>
                                <select
                                    data-testid="contact-input-budget"
                                    className="exec-input"
                                    value={form.budget_range}
                                    onChange={(e) => update("budget_range", e.target.value)}
                                >
                                    <option value="">Select…</option>
                                    {BUDGETS.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="exec-input-label">Timeline</label>
                                <select
                                    data-testid="contact-input-timeline"
                                    className="exec-input"
                                    value={form.timeline}
                                    onChange={(e) => update("timeline", e.target.value)}
                                >
                                    <option value="">Select…</option>
                                    {TIMELINES.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="exec-input-label">How can we help?</label>
                                <textarea
                                    required
                                    rows={5}
                                    data-testid="contact-input-message"
                                    className="exec-input resize-none"
                                    value={form.message}
                                    onChange={(e) => update("message", e.target.value)}
                                    placeholder="Tell us about your project, goals, and where you're stuck."
                                />
                            </div>
                            {error && (
                                <p className="md:col-span-2 text-sm text-red-600" data-testid="contact-error">
                                    {error}
                                </p>
                            )}
                            <div className="md:col-span-2 pt-6">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="contact-submit"
                                    className="inline-flex items-center gap-3 bg-[#0A192F] text-white hover:bg-[#1E3A8A] disabled:opacity-60 transition-colors rounded-sm px-10 py-4 font-bold text-sm uppercase tracking-widest"
                                >
                                    {submitting ? "Sending…" : "Send Message"}
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                                <p className="mt-4 text-xs text-slate-500">
                                    By submitting, you agree to be contacted by
                                    Hopewell Consulting regarding your inquiry.
                                </p>
                            </div>
                        </form>
                    )}
                </div>
            </section>

            <section className="bg-slate-50 py-16 border-t border-slate-200">
                <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-3 gap-8 text-center">
                    <div>
                        <Phone className="w-5 h-5 text-[#D4AF37] mx-auto" />
                        <a
                            href={`tel:${SITE.phoneLink}`}
                            className="mt-3 block font-serif text-xl font-bold text-[#0A192F]"
                        >
                            {SITE.phone}
                        </a>
                    </div>
                    <div>
                        <Mail className="w-5 h-5 text-[#D4AF37] mx-auto" />
                        <a
                            href={`mailto:${SITE.email}`}
                            className="mt-3 block font-serif text-lg font-bold text-[#0A192F] break-all"
                        >
                            {SITE.email}
                        </a>
                    </div>
                    <div>
                        <MapPin className="w-5 h-5 text-[#D4AF37] mx-auto" />
                        <p className="mt-3 font-serif text-xl font-bold text-[#0A192F]">
                            {SITE.address}
                        </p>
                        <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
                            Serving {SITE.serviceAreas.join(" · ")}
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}
